import { useState } from 'react';
import type { VoucherBalance } from '@salonomy/types';
import { Button, Field, Modal, Pill, TextInput } from '@salonomy/ui';
import { useRedeemVoucher, useVoucherBalanceByCode } from '../../data/vouchers.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from './money.js';
import './till.css';

/**
 * WS9 §2 — redeem a voucher as a TENDER at checkout (surface a). Enter/scan a code → show the server
 * balance → apply full or partial against the basket. Redemption is a tender (like card/cash), NEVER a
 * discount line; the API decrements the double-entry liability and recognises revenue/VAT on the real
 * redeemed line (ADR-0009).
 *
 * HONESTY: every number shown is a server number. The lookup balance is the ledger-derived `balancePence`;
 * the redeemable cap is `min(balance, dueNow)` (both server values), and after a successful redeem the
 * caller records the server-returned tendered amount — the UI never recomputes a balance or a basket
 * total. Redeem is idempotent on a per-intent key minted once on submit; the button is disabled while
 * pending so a double-tap can't fire a second redeem, and a 422/409 (e.g. expired, over-balance) surfaces
 * the API message faithfully.
 */
export function VoucherRedeemModal({
  appointmentId,
  clientId,
  serviceName,
  dueNowPence,
  onRedeemed,
  onClose,
}: {
  appointmentId: string;
  clientId: string | null;
  /** A description for the redeemed line — the supply the voucher is tendered against. */
  serviceName: string;
  /** The basket's outstanding amount (a server number from the appointment), the redeemable cap. */
  dueNowPence: number;
  /** Called with the server-confirmed amount redeemed (pence) so the till records the tender. */
  onRedeemed: (amountPence: number) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const redeem = useRedeemVoucher();

  const [draft, setDraft] = useState('');
  const [code, setCode] = useState<string | undefined>(undefined);
  const { data: balance, isFetching } = useVoucherBalanceByCode(code);

  function lookup() {
    const next = draft.trim();
    if (next) setCode(next);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Redeem voucher"
      width={440}
      footer={
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <Field
        label="Voucher / gift-card code"
        hint="Type or scan the code from the card or receipt."
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <TextInput
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') lookup();
            }}
            placeholder="e.g. GC-7Q4M-2KX9"
            style={{ textTransform: 'uppercase' }}
          />
          <Button onClick={lookup} disabled={!draft.trim()}>
            Look up
          </Button>
        </div>
      </Field>

      {code && !isFetching && balance === null ? (
        <div className="muted" style={{ marginTop: 12, fontSize: 13.5 }}>
          No active voucher matches that code. Check the code and try again.
        </div>
      ) : null}

      {balance ? (
        balance.status !== 'active' || balance.balancePence <= 0 ? (
          <div style={{ marginTop: 12 }}>
            <Pill tone="danger">{balance.status !== 'active' ? balance.status : 'no balance'}</Pill>
            <div className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
              This voucher can&rsquo;t be redeemed right now.
            </div>
          </div>
        ) : (
          <RedeemPanel
            key={balance.voucherId}
            balance={balance}
            appointmentId={appointmentId}
            clientId={clientId}
            serviceName={serviceName}
            dueNowPence={dueNowPence}
            pending={redeem.isPending}
            onSubmit={async (amountPence) => {
              try {
                // Mint the idempotency key once per intent; the button is disabled while pending so a
                // double-tap can't reach here a second time, and the API is idempotent on this key.
                const updated = await redeem.mutateAsync({
                  id: balance.voucherId,
                  body: {
                    amountPence,
                    redeemedLines: [
                      {
                        description: serviceName,
                        qty: 1,
                        unitPricePence: amountPence,
                      },
                    ],
                    clientId: clientId ?? undefined,
                    idempotencyKey: crypto.randomUUID(),
                  },
                });
                // Record the tender from the server's view (the amount the liability moved by), then
                // close. We don't recompute the new balance here — the hook already cached `updated`.
                onRedeemed(amountPence);
                toast.success(
                  `Redeemed ${money(amountPence)} · ${money(updated.balancePence)} left on card`,
                );
                onClose();
              } catch (err) {
                toast.error(
                  err instanceof ApiClientError ? err.message : 'Could not redeem the voucher',
                );
              }
            }}
          />
        )
      ) : null}
    </Modal>
  );
}

/**
 * The redeem-amount step, shown once a live voucher is found. The cap is `min(balance, dueNow)` — both
 * server numbers — and the amount input defaults to the cap (the common "use it all" tap). The user can
 * type a smaller partial amount; the field never lets the intent exceed the cap (the server re-checks).
 */
function RedeemPanel({
  balance,
  serviceName,
  dueNowPence,
  pending,
  onSubmit,
}: {
  balance: VoucherBalance;
  appointmentId: string;
  clientId: string | null;
  serviceName: string;
  dueNowPence: number;
  pending: boolean;
  onSubmit: (amountPence: number) => void;
}) {
  const cap = Math.min(balance.balancePence, dueNowPence);
  const [amount, setAmount] = useState(penceToPounds(cap));
  const parsed = poundsToPence(amount);
  const valid = parsed != null && parsed > 0 && parsed <= cap;

  return (
    <div className="till-voucher__found" style={{ marginTop: 12 }}>
      <div className="till__line">
        <span>Balance on card</span>
        <span className="mono">{money(balance.balancePence)}</span>
      </div>
      <div className="till__line">
        <span>Due now</span>
        <span className="mono">{money(dueNowPence)}</span>
      </div>
      <div className="till__line till__line--total">
        <span>Can tender up to</span>
        <span className="mono">{money(cap)}</span>
      </div>

      <Field
        label="Amount to redeem (£)"
        hint={`Full or partial, up to ${money(cap)}. Applied as a tender against ${serviceName}.`}
        error={parsed != null && parsed > cap ? `Can’t redeem more than ${money(cap)}.` : null}
      >
        <TextInput
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />
      </Field>

      <Button
        block
        size="lg"
        disabled={!valid || pending}
        onClick={() => {
          if (valid && parsed != null) onSubmit(parsed);
        }}
      >
        {pending ? 'Redeeming…' : `Redeem ${parsed != null && valid ? money(parsed) : ''}`}
      </Button>
    </div>
  );
}
